import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSubProcessList } from "../../../Store/moduleSlice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiDatabase,
  FiTrendingUp,
  FiShield,
  FiBarChart2,
  FiChevronRight,
  FiLayers,
  FiLock,
  FiActivity,
  FiArrowLeft,
  FiGrid,
  FiList,
  FiSearch,
} from "react-icons/fi";

const SubSubProcessModule = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

const {
  moduleName = "GLOBAL PROCESS",
  processName = "General Accounting & Reporting",
  technicalName = "",
} = location.state || {};


  const [viewMode, setViewMode] = useState("grid");
  const [searchTerm, setSearchTerm] = useState("");

  const { subProcessList, loading, error } = useSelector(
    (state) => state.module,
  );

 useEffect(() => {
  if (moduleName && processName) {
    dispatch(
      fetchSubProcessList({
        moduleName,
        processName,
      }),
    );
  }
}, [dispatch, moduleName, processName]);

  const formatName = (name) =>
    name
      ?.toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // Get icon based on module name or index
  const getModuleIcon = (name, index) => {
    const icons = [
      <FiDatabase className="w-5 h-5" />,
      <FiTrendingUp className="w-5 h-5" />,
      <FiShield className="w-5 h-5" />,
      <FiBarChart2 className="w-5 h-5" />,
      <FiLayers className="w-5 h-5" />,
      <FiLock className="w-5 h-5" />,
      <FiActivity className="w-5 h-5" />,
    ];
    return icons[index % icons.length];
  };

  // Get gradient based on module index
  const getCardGradient = (index) => {
    const gradients = [
      "from-blue-500 to-blue-600",
      "from-purple-500 to-purple-600",
      "from-emerald-500 to-emerald-600",
      "from-orange-500 to-orange-600",
      "from-red-500 to-red-600",
      "from-indigo-500 to-indigo-600",
      "from-teal-500 to-teal-600",
    ];
    return gradients[index % gradients.length];
  };

  const handleClick = (item) => {
    navigate("/data-injection/process-details", {
      state: {
        moduleName,
        processName,
        subProcessName: item.name,
        technicalName
      },
    });
  };

  //   const handleBack = () => {
  //   if (window.history.length > 1) {
  //     navigate(-1); // go to previous page
  //   } else {
  //     navigate("/"); // fallback → Dashboard
  //   }
  // };

  // Filter based on search
  const filteredProcesses =
    subProcessList?.filter((item) =>
      formatName(item.name)?.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  if (loading) {
    return (
     <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header Section */}
      <div className="relative bg-gradient-to-r from-[#36096d] via-[#4a0e8f] to-[#37d5d6] text-white rounded-2xl shadow-xl border-b-4 border-[#37d5d6] overflow-hidden mb-6">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#36096d]/20 via-[#37d5d6]/10 to-[#36096d]/20 animate-pulse"></div>

        {/* Glass morphism effect */}
        <div className="absolute inset-0 backdrop-blur-[2px]"></div>

        <div className="relative p-5 z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                {/* Dynamic title based on process name */}
                <h1 className="text-2xl lg:text-2xl font-bold text-white">
                  {formatName(processName)} 
                </h1>
              </div>
            </div>

            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-white/10 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border border-white/20 hover:border-white/40"
            >
              <FiArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Back
            </button>
          </div>
        </div>

        {/* Bottom shine effect */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#37d5d6] to-transparent"></div>
      </div>

      {/* Content based on view mode */}
      {filteredProcesses.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <FiDatabase className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No sub-processes found
          </h3>
          <p className="text-gray-500">
            No sub-processes match your search criteria.
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="mt-4 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Clear search
            </button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        // Grid View - Fixed: Using subProcessList instead of moduleName
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProcesses.map((item, idx) => (
            <div
              key={item.technicalName || idx}
              onClick={() => handleClick(item)}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
            >
              {/* Gradient Accent Bar */}
              <div
                className={`h-2 bg-gradient-to-r ${getCardGradient(idx)}`}
              ></div>

              {/* Card Content */}
              <div className="p-6">
                {/* Icon Section */}
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-r ${getCardGradient(
                    idx,
                  )} bg-opacity-10 flex items-center justify-center mb-4 text-white`}
                >
                  {getModuleIcon(item.name, idx)}
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-1">
                  {formatName(item.name)}
                </h3>

                {/* Description */}
                <p className="text-gray-500 text-sm mb-4 line-clamp-2">
                  Manage and inject audit data for{" "}
                  {formatName(item.name).toLowerCase()} processes
                </p>

                {/* Arrow Icon */}
                <div className="flex items-center justify-end">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-500 transition-colors duration-300">
                    <FiChevronRight className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                </div>
              </div>

              {/* Decorative Background Element */}
              <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-gradient-to-r from-gray-50 to-transparent rounded-full opacity-50 group-hover:scale-150 transition-transform duration-500"></div>
            </div>
          ))}
        </div>
      ) : (
        // List View
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {filteredProcesses.map((item, idx) => (
              <div
                key={item.technicalName || idx}
                onClick={() => handleClick(item)}
                className="group flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div
                    className={`w-10 h-10 rounded-lg bg-gradient-to-r ${getCardGradient(
                      idx,
                    )} bg-opacity-10 flex items-center justify-center`}
                  >
                    {getModuleIcon(item.name, idx)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800 text-sm">
                      {formatName(item.name)}
                    </h3>
                    <p className="text-gray-500 text-xs mt-0.5">
                      Configure and manage data injection workflows
                    </p>
                  </div>
                </div>
                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all ml-4" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubSubProcessModule;
